import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Doctor } from '../../types';
import { doctorService } from '../../services/doctorService';
import DoctorsStats from './DoctorsStats';
import DoctorsList from './DoctorsList';
import DoctorForm from './DoctorForm';
import DoctorViewDialog from './DoctorViewDialog';

const Doctors: React.FC = () => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'approved' | 'pending'>('all');
  const [selectedDoctors, setSelectedDoctors] = useState<number[]>([]);

  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingDoctor, setViewingDoctor] = useState<Doctor | null>(null);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    specialization: '',
    isActive: true,
    password: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    departmentId: undefined as number | undefined,
    experience: '',
    qualifications: '',
    licenseNumber: '',
    aboutYourself: '',
    profileImage: null as File | null,
    // Services
    services: {
      chat: { price: 0, isEnabled: true, duration: 30, description: '' },
      audioCall: { price: 0, isEnabled: true, duration: 30, description: '' },
      videoCall: { price: 0, isEnabled: true, duration: 30, description: '' },
    },
    // Degrees
    degrees: [] as Array<{ degree: string; institution: string; year: string }>,
    // Availability - weekly schedule with multiple time ranges per day
    availability: [
      { day: 'monday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
      { day: 'tuesday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
      { day: 'wednesday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
      { day: 'thursday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
      { day: 'friday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
      { day: 'saturday', isAvailable: false, timeRanges: [] },
      { day: 'sunday', isAvailable: false, timeRanges: [] },
    ],
    // Ratings & Reviews
    ratings: [] as Array<{ patientId?: number | null; patientName?: string; patientProfileImage?: string; rating: number; message: string }>,
  });

  // Fetch doctors from API
  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        page,
        limit,
        search,
      };
      
      if (statusFilter === 'active') {
        params.isActive = true;
      } else if (statusFilter === 'inactive') {
        params.isActive = false;
      } else if (statusFilter === 'approved') {
        params.isApproved = true;
      } else if (statusFilter === 'pending') {
        params.isApproved = false;
      }

      const response = await doctorService.getDoctors(params);
      setDoctors(response.data.doctors);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch doctors.');
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  // Reset page to 1 when search or filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  // Fetch doctors when component mounts or dependencies change
  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleOpen = async (doctor?: Doctor) => {
    setOpen(true);
    if (doctor) {
      setEditingDoctor(doctor);
      setLoadingFormData(true);
      
      // Load existing services, degrees, and availability
      let services = {
        chat: { price: 0, isEnabled: true, duration: 30, description: '' },
        audioCall: { price: 0, isEnabled: true, duration: 30, description: '' },
        videoCall: { price: 0, isEnabled: true, duration: 30, description: '' },
      };
      let degrees: Array<{ degree: string; institution: string; year: string }> = [];
      let availability = [
        { day: 'monday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
        { day: 'tuesday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
        { day: 'wednesday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
        { day: 'thursday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
        { day: 'friday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
        { day: 'saturday', isAvailable: false, timeRanges: [] },
        { day: 'sunday', isAvailable: false, timeRanges: [] },
      ];
      let ratings: Array<{ patientId?: number | null; patientName?: string; patientProfileImage?: string; rating: number; message: string; id?: number }> = [];

      try {
        // Fetch doctor profile with services and availability
        const { getApiUrl } = await import('../../config/api');
        const response = await fetch(getApiUrl(`/api/doctor/${doctor.id}/profile`), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            // Load services
            if (data.data.services) {
              services = {
                chat: {
                  price: data.data.services.chat?.price || 0,
                  isEnabled: data.data.services.chat?.isEnabled ?? true,
                  duration: data.data.services.chat?.duration || 30,
                  description: data.data.services.chat?.description || '',
                },
                audioCall: {
                  price: data.data.services.audioCall?.price || 0,
                  isEnabled: data.data.services.audioCall?.isEnabled ?? true,
                  duration: data.data.services.audioCall?.duration || 30,
                  description: data.data.services.audioCall?.description || '',
                },
                videoCall: {
                  price: data.data.services.videoCall?.price || 0,
                  isEnabled: data.data.services.videoCall?.isEnabled ?? true,
                  duration: data.data.services.videoCall?.duration || 30,
                  description: data.data.services.videoCall?.description || '',
                },
              };
            }
            // Load degrees
            if (data.data.doctor?.degrees && Array.isArray(data.data.doctor.degrees)) {
              degrees = data.data.doctor.degrees.map((d: any) => ({
                degree: d.degree || d.name || '',
                institution: d.institution || '',
                year: d.year || '',
              }));
            }
            // Load availability - fetch from admin slots API
            try {
              const slotsResponse = await fetch(getApiUrl(`/admin/doctors/${doctor.id}/slots`), {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
                },
              });
              if (slotsResponse.ok) {
                const slotsData = await slotsResponse.json();
                if (slotsData.success && slotsData.data?.weekSchedule) {
                  availability = slotsData.data.weekSchedule.map((daySchedule: any) => ({
                    day: daySchedule.day,
                    isAvailable: daySchedule.isAvailable,
                    timeRanges: daySchedule.timeRanges || [],
                  }));
                }
              }
            } catch (err) {
              console.error('Failed to load time slots:', err);
              // If loading fails, keep default availability structure
            }

            // Load ratings/reviews - fetch from admin reviews API
            try {
              const reviewsResponse = await fetch(getApiUrl(`/admin/doctors/${doctor.id}/reviews`), {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
                },
              });
              if (reviewsResponse.ok) {
                const reviewsData = await reviewsResponse.json();
                if (reviewsData.success && reviewsData.data?.reviews) {
                  ratings = reviewsData.data.reviews.map((review: any) => ({
                    id: review.id,
                    patientId: review.patientId,
                    patientName: review.patientName || '',
                    patientProfileImage: review.patientProfileImage || '',
                    rating: review.rating,
                    message: review.message || '',
                  }));
                }
              }
            } catch (err) {
              console.error('Failed to load reviews:', err);
              // If loading fails, keep empty ratings array
            }
          }
        }
      } catch (error) {
        console.error('Failed to load doctor details:', error);
      } finally {
        setLoadingFormData(false);
      }

      setFormData({
        fullName: doctor.fullName || '',
        email: doctor.email || '',
        phoneNumber: doctor.phoneNumber || '',
        specialization: doctor.specialization || '',
        isActive: doctor.isActive,
        password: '',
        dateOfBirth: doctor.dateOfBirth ? new Date(doctor.dateOfBirth).toISOString().split('T')[0] : '',
        gender: doctor.gender || '',
        address: doctor.address || '',
        departmentId: doctor.departmentId,
        experience: doctor.experience || '',
        qualifications: doctor.qualifications || '',
        licenseNumber: doctor.licenseNumber || '',
        aboutYourself: doctor.aboutYourself || '',
        profileImage: null,
        services,
        degrees,
        availability,
        ratings,
      });
    } else {
      setEditingDoctor(null);
      setLoadingFormData(false);
      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        specialization: '',
        isActive: true,
        password: '',
        dateOfBirth: '',
        gender: '',
        address: '',
        departmentId: undefined,
        experience: '',
        qualifications: '',
        licenseNumber: '',
        aboutYourself: '',
        profileImage: null,
        services: {
          chat: { price: 0, isEnabled: true, duration: 30, description: '' },
          audioCall: { price: 0, isEnabled: true, duration: 30, description: '' },
          videoCall: { price: 0, isEnabled: true, duration: 30, description: '' },
        },
        degrees: [],
        availability: [
          { day: 'monday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
          { day: 'tuesday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
          { day: 'wednesday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
          { day: 'thursday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
          { day: 'friday', isAvailable: true, timeRanges: [{ startTime: '09:00 AM', endTime: '05:00 PM', slotDuration: 30 }] },
          { day: 'saturday', isAvailable: false, timeRanges: [] },
          { day: 'sunday', isAvailable: false, timeRanges: [] },
        ],
        ratings: [],
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingDoctor(null);
  };

  const handleView = (doctor: Doctor) => {
    setViewingDoctor(doctor);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewingDoctor(null);
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const doctorData: any = {
        fullName: formData.fullName.trim() || undefined,
        email: formData.email.trim() || undefined,
        phoneNumber: formData.phoneNumber.trim() || undefined,
        specialization: formData.specialization.trim() || undefined,
        isActive: formData.isActive,
        dateOfBirth: formData.dateOfBirth || undefined,
        gender: formData.gender || undefined,
        address: formData.address.trim() || undefined,
        departmentId: formData.departmentId,
        experience: formData.experience.trim() || undefined,
        qualifications: formData.qualifications.trim() || undefined,
        licenseNumber: formData.licenseNumber.trim() || undefined,
        aboutYourself: formData.aboutYourself.trim() || undefined,
      };

      // Remove undefined values
      Object.keys(doctorData).forEach(key => {
        if (doctorData[key] === undefined || doctorData[key] === '') {
          delete doctorData[key];
        }
      });

      // Handle password (optional)
      const trimmedPassword = formData.password.trim();
      if (trimmedPassword) {
        doctorData.password = trimmedPassword;
      }

      // Handle profile image upload
      if (formData.profileImage) {
        // For now, we'll need to handle file upload separately
        // This will be handled in the service layer
        doctorData.profileImage = formData.profileImage;
      }

      if (editingDoctor) {
        await doctorService.updateDoctor(editingDoctor.id, doctorData);
        
        // Update services, degrees, and availability separately
        const doctorId = editingDoctor.id;
        const { getApiUrl } = await import('../../config/api');
        
        // Update services
        try {
          const servicesResponse = await fetch(getApiUrl(`/admin/doctors/${doctorId}/services`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({
              chat: formData.services.chat,
              audioCall: formData.services.audioCall,
              videoCall: formData.services.videoCall,
            }),
          });
          if (!servicesResponse.ok) {
            const errorData = await servicesResponse.json().catch(() => ({}));
            console.error('Failed to update services:', errorData);
          }
        } catch (err) {
          console.error('Failed to update services:', err);
        }

        // Update degrees
        try {
          const degreesResponse = await fetch(getApiUrl(`/admin/doctors/${doctorId}/degrees`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({ degrees: formData.degrees }),
          });
          if (!degreesResponse.ok) {
            const errorData = await degreesResponse.json().catch(() => ({}));
            console.error('Failed to update degrees:', errorData);
          }
        } catch (err) {
          console.error('Failed to update degrees:', err);
        }

        // Update availability
        try {
          const weekSchedule = formData.availability.map(day => ({
            day: day.day,
            isAvailable: day.isAvailable,
            timeRanges: day.isAvailable ? (day.timeRanges || []) : [],
          }));
          
          await fetch(getApiUrl(`/admin/doctors/${doctorId}/slots`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({ weekSchedule }),
          });
        } catch (err) {
          console.error('Failed to update availability:', err);
        }

        // Update ratings/reviews
        try {
          const ratingsResponse = await fetch(getApiUrl(`/admin/doctors/${doctorId}/reviews`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({ ratings: formData.ratings }),
          });
          if (!ratingsResponse.ok) {
            const errorData = await ratingsResponse.json().catch(() => ({}));
            console.error('Failed to update reviews:', errorData);
          }
        } catch (err) {
          console.error('Failed to update reviews:', err);
        }

        setSuccessMessage('Doctor updated successfully!');
      } else {
        // For new doctors, email, phoneNumber, and fullName are required
        if (!doctorData.email || !doctorData.phoneNumber || !doctorData.fullName) {
          setError('Name, email, and phone number are required for new doctors.');
          setSubmitting(false);
          return;
        }
        const response = await doctorService.createDoctor(doctorData);
        const newDoctorId = response.data?.id;
        
        if (!newDoctorId) {
          throw new Error('Failed to get doctor ID from creation response');
        }
        
        // Update services, degrees, and availability for new doctor
        const { getApiUrl } = await import('../../config/api');
        
        // Update services
        try {
          const servicesResponse = await fetch(getApiUrl(`/admin/doctors/${newDoctorId}/services`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({
              chat: formData.services.chat,
              audioCall: formData.services.audioCall,
              videoCall: formData.services.videoCall,
            }),
          });
          if (!servicesResponse.ok) {
            const errorData = await servicesResponse.json().catch(() => ({}));
            console.error('Failed to update services:', errorData);
            throw new Error(`Failed to update services: ${errorData.message || 'Unknown error'}`);
          }
          console.log('Services updated successfully for doctor:', newDoctorId);
        } catch (err: any) {
          console.error('Failed to update services:', err);
          throw new Error(`Failed to update services: ${err.message || 'Unknown error'}`);
        }

        // Update degrees
        try {
          const degreesResponse = await fetch(getApiUrl(`/admin/doctors/${newDoctorId}/degrees`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({ degrees: formData.degrees }),
          });
          if (!degreesResponse.ok) {
            const errorData = await degreesResponse.json().catch(() => ({}));
            console.error('Failed to update degrees:', errorData);
            throw new Error(`Failed to update degrees: ${errorData.message || 'Unknown error'}`);
          }
          console.log('Degrees updated successfully for doctor:', newDoctorId);
        } catch (err: any) {
          console.error('Failed to update degrees:', err);
          throw new Error(`Failed to update degrees: ${err.message || 'Unknown error'}`);
        }

        // Update availability
        try {
          const weekSchedule = formData.availability.map(day => ({
            day: day.day,
            isAvailable: day.isAvailable,
            timeRanges: day.isAvailable ? (day.timeRanges || []) : [],
          }));
          
          const slotsResponse = await fetch(getApiUrl(`/admin/doctors/${newDoctorId}/slots`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({ weekSchedule }),
          });
          if (!slotsResponse.ok) {
            const errorData = await slotsResponse.json().catch(() => ({}));
            console.error('Failed to update availability:', errorData);
            throw new Error(`Failed to update availability: ${errorData.message || 'Unknown error'}`);
          }
          console.log('Availability updated successfully for doctor:', newDoctorId);
        } catch (err: any) {
          console.error('Failed to update availability:', err);
          throw new Error(`Failed to update availability: ${err.message || 'Unknown error'}`);
        }

        // Update ratings/reviews
        try {
          const ratingsResponse = await fetch(getApiUrl(`/admin/doctors/${newDoctorId}/reviews`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
            },
            body: JSON.stringify({ ratings: formData.ratings }),
          });
          if (!ratingsResponse.ok) {
            const errorData = await ratingsResponse.json().catch(() => ({}));
            console.error('Failed to update reviews:', errorData);
            throw new Error(`Failed to update reviews: ${errorData.message || 'Unknown error'}`);
          }
          console.log('Reviews updated successfully for doctor:', newDoctorId);
        } catch (err: any) {
          console.error('Failed to update reviews:', err);
          throw new Error(`Failed to update reviews: ${err.message || 'Unknown error'}`);
        }

        setSuccessMessage('Doctor created successfully!');
      }

      await fetchDoctors();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save doctor.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this doctor?')) {
      try {
        setLoading(true);
        setError('');
        await doctorService.deleteDoctor(id);
        setSuccessMessage('Doctor deleted successfully!');
        await fetchDoctors();
      } catch (err: any) {
        setError(err.message || 'Failed to delete doctor.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusToggle = async (id: number, isActive: boolean) => {
    try {
      setLoading(true);
      setError('');
      await doctorService.updateDoctorStatus(id, isActive);
      setSuccessMessage(`Doctor ${isActive ? 'activated' : 'deactivated'} successfully!`);
      await fetchDoctors();
    } catch (err: any) {
      setError(err.message || 'Failed to update doctor status.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      if (field === 'departmentId' && value === null) {
        return { ...prev, [field]: undefined };
      }
      return { ...prev, [field]: value };
    });
  };

  // Handle doctor selection
  const handleSelectDoctor = (doctorId: number) => {
    setSelectedDoctors(prev => 
      prev.includes(doctorId) 
        ? prev.filter(id => id !== doctorId)
        : [...prev, doctorId]
    );
  };

  // Handle select all doctors
  const handleSelectAllDoctors = () => {
    setSelectedDoctors(
      selectedDoctors.length === doctors.length 
        ? [] 
        : doctors.map(d => d.id)
    );
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (isActive: boolean) => {
    if (selectedDoctors.length === 0) return;
    
    try {
      setLoading(true);
      setError('');
      await doctorService.bulkUpdateDoctors(selectedDoctors, { isActive });
      setSuccessMessage(`${selectedDoctors.length} doctors ${isActive ? 'activated' : 'deactivated'} successfully!`);
      setSelectedDoctors([]);
      await fetchDoctors();
    } catch (err: any) {
      setError(err.message || 'Failed to update doctors status.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Doctors</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          disabled={loading}
          sx={{
            backgroundColor: '#00A76F',
            '&:hover': {
              backgroundColor: '#007867',
            },
          }}
        >
          Add Doctor
        </Button>
      </Box>

      {/* Success and Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <DoctorsStats doctors={doctors} />
          <DoctorsList 
            doctors={doctors}
            onEdit={handleOpen}
            onDelete={handleDelete}
            onStatusToggle={handleStatusToggle}
            onView={handleView}
            loading={loading}
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            selectedDoctors={selectedDoctors}
            onSelectDoctor={handleSelectDoctor}
            onSelectAllDoctors={handleSelectAllDoctors}
            onBulkStatusUpdate={handleBulkStatusUpdate}
          />
        </>
      )}

      <DoctorForm
        open={open}
        onClose={handleClose}
        error={error}
        onSave={handleSave}
        doctor={editingDoctor}
        formData={formData}
        onInputChange={handleInputChange}
        loadingFormData={loadingFormData}
        submitting={submitting}
      />

      <DoctorViewDialog
        open={viewOpen}
        onClose={handleCloseView}
        doctor={viewingDoctor}
      />
    </Box>
  );
};

export default Doctors;
